import { db } from "@/lib/firebase";
import { GoogleAnalyticsAccount } from "@/schemas/firestore/googleAnalytics.schema";
import { useStore } from "@/store";
import { FirebaseGoogleAnalyticsAccount } from "@/types/GoogleAnalytics";
import {
  collection,
  doc,
  onSnapshot,
  query,
  Timestamp,
  Unsubscribe,
} from "firebase/firestore";
import { useCallback, useEffect, useRef } from "react";

export const useGoogleAnalyticsData = (userId: string | null) => {
  const {
    setAccounts,
    addAccount,
    updateAccount,
    removeAccount,
    setUIState,
    setErrorForAccount,
    accounts,
  } = useStore();

  const unsubscribeRef = useRef<Unsubscribe | null>(null);
  const lastUserIdRef = useRef<string | null>(null);

  const convertFirebaseAccount = (
    firebaseAccount: FirebaseGoogleAnalyticsAccount,
  ): GoogleAnalyticsAccount => ({
    account_id: firebaseAccount.account_id,
    account_email: firebaseAccount.account_email,
    account_name: firebaseAccount.account_name ?? null,
    token_ref: firebaseAccount.token_ref ?? null,
    properties: firebaseAccount.properties ?? [],
    client_id: firebaseAccount.client_id,
    status: firebaseAccount.status ?? "connected",
    error_message: firebaseAccount.error_message ?? null,
    created_at: firebaseAccount.created_at ?? null,
    updated_at: firebaseAccount.updated_at ?? null,
  });

  // Subscribe to all Google Analytics accounts for the user
  const subscribeToAccounts = useCallback(() => {
    if (!userId) {
      console.log("GA Data Hook: No userId, setting empty accounts");
      setAccounts([]);
      return;
    }

    console.log(
      "GA Data Hook: Starting account subscription for user:",
      userId,
    );
    setUIState({ isLoadingAccounts: true });

    try {
      const accountsRef = collection(
        db,
        `users/${userId}/integrations/google_analytics/accounts`,
      );
      console.log(
        "GA Data Hook: Created accounts ref:",
        `users/${userId}/integrations/google_analytics/accounts`,
      );
      const accountsQuery = query(accountsRef);

      const unsubscribe = onSnapshot(
        accountsQuery,
        (snapshot) => {
          console.log(
            "GA Data Hook: Accounts snapshot received with",
            snapshot.size,
            "documents",
          );
          const accountsData: GoogleAnalyticsAccount[] = [];

          snapshot.forEach((doc) => {
            const data = doc.data() as Omit<
              FirebaseGoogleAnalyticsAccount,
              "account_id"
            >;
            const firebaseAccount: FirebaseGoogleAnalyticsAccount = {
              ...data,
              account_id: doc.id,
              created_at:
                data.created_at instanceof Timestamp
                  ? data.created_at.toDate()
                  : data.created_at
                    ? new Date(data.created_at)
                    : undefined,
              updated_at:
                data.updated_at instanceof Timestamp
                  ? data.updated_at.toDate()
                  : data.updated_at
                    ? new Date(data.updated_at)
                    : undefined,
            };

            accountsData.push(convertFirebaseAccount(firebaseAccount));
          });

          setAccounts(accountsData);
          setUIState({ isLoadingAccounts: false });
        },
        (error) => {
          console.error(
            "GA Data Hook: Firebase accounts subscription error:",
            error,
          );
          setUIState({ isLoadingAccounts: false });
        },
      );

      unsubscribeRef.current = unsubscribe;
    } catch (error) {
      console.error("Failed to subscribe to accounts:", error);
      setUIState({ isLoadingAccounts: false });
    }
  }, [userId, setAccounts, setUIState]);

  // Subscribe to a specific account
  const subscribeToAccount = useCallback(
    (accountId: string) => {
      if (!userId) return null;

      const accountRef = doc(
        db,
        `users/${userId}/integrations/google_analytics/accounts/${accountId}`,
      );

      const unsubscribe = onSnapshot(
        accountRef,
        (doc) => {
          if (doc.exists()) {
            const data = doc.data() as Omit<
              FirebaseGoogleAnalyticsAccount,
              "account_id"
            >;
            const firebaseAccount: FirebaseGoogleAnalyticsAccount = {
              ...data,
              account_id: doc.id,
              created_at:
                data.created_at instanceof Timestamp
                  ? data.created_at.toDate()
                  : data.created_at
                    ? new Date(data.created_at)
                    : undefined,
              updated_at:
                data.updated_at instanceof Timestamp
                  ? data.updated_at.toDate()
                  : data.updated_at
                    ? new Date(data.updated_at)
                    : undefined,
            };

            const account = convertFirebaseAccount(firebaseAccount);

            // Check if this is a new account or an update
            const existingAccount = accounts.find(
              (acc) => acc.account_id === accountId,
            );
            if (existingAccount) {
              updateAccount(accountId, account);
            } else {
              addAccount(account);
            }
          } else {
            // Account was deleted
            removeAccount(accountId);
          }
        },
        (error) => {
          console.error(
            `Firebase account ${accountId} subscription error:`,
            error,
          );
          setErrorForAccount(
            accountId,
            `Failed to sync account: ${error.message}`,
          );
        },
      );

      return unsubscribe;
    },
    [
      userId,
      accounts,
      addAccount,
      updateAccount,
      removeAccount,
      setErrorForAccount,
    ],
  );

  // Get account by ID
  const getAccountByIdCallback = useCallback(
    (accountId: string): GoogleAnalyticsAccount | null => {
      return (
        accounts.find((account) => account.account_id === accountId) || null
      );
    },
    [accounts],
  );

  // Get all connected accounts
  const getConnectedAccounts = useCallback((): GoogleAnalyticsAccount[] => {
    return accounts.filter((account) => account.status === "connected");
  }, [accounts]);

  // Get accounts with errors
  const getAccountsWithErrors = useCallback((): GoogleAnalyticsAccount[] => {
    return accounts.filter((account) => account.status === "error");
  }, [accounts]);

  // Check if user has any connected accounts
  const hasConnectedAccountsCallback = useCallback((): boolean => {
    return getConnectedAccounts().length > 0;
  }, [getConnectedAccounts]);

  // Get account count
  const getAccountCountCallback = useCallback((): number => {
    return accounts.length;
  }, [accounts]);

  // Get properties for a specific account
  const getPropertiesForAccountCallback = useCallback(
    (accountId: string) => {
      const account = getAccountByIdCallback(accountId);
      return account?.properties || [];
    },
    [getAccountByIdCallback],
  );

  // Subscribe to accounts when userId changes
  useEffect(() => {
    // Only re-subscribe if userId actually changed
    if (lastUserIdRef.current === userId) {
      return;
    }

    console.log(
      "GA Data Hook: UserId changed from",
      lastUserIdRef.current,
      "to",
      userId,
    );
    lastUserIdRef.current = userId;

    // Clean up previous subscription
    if (unsubscribeRef.current) {
      console.log("GA Data Hook: Cleaning up previous accounts subscription");
      unsubscribeRef.current();
      unsubscribeRef.current = null;
    }

    if (userId) {
      subscribeToAccounts();
    } else {
      console.log("GA Data Hook: No userId, clearing accounts");
      setAccounts([]);
    }

    // Cleanup on unmount
    return () => {
      if (unsubscribeRef.current) {
        unsubscribeRef.current();
        unsubscribeRef.current = null;
      }
    };
  }, [userId, subscribeToAccounts, setAccounts]);

  return {
    accounts,
    subscribeToAccount,
    getAccountById: getAccountByIdCallback,
    getConnectedAccounts,
    getAccountsWithErrors,
    hasConnectedAccounts: hasConnectedAccountsCallback,
    getAccountCount: getAccountCountCallback,
    getPropertiesForAccount: getPropertiesForAccountCallback,
  };
};
